import { useSyncExternalStore } from 'react';

/**
 * Which steps of an answer the user has crossed out as done (clicking a step or checklist row strikes
 * it through and greys its marker — the Figma comment on 21746:18244).
 *
 * Kept in localStorage so ticking off a procedure survives closing the panel, a reload, and the
 * docked ⇄ floating switch (which remounts the answer). Keyed by a hash of the answer's markdown: a
 * live answer and the same answer loaded back from history have different ids and timestamps, but the
 * same text. Only the most recent answers are kept, so the entry cannot grow without bound.
 */

const STORAGE_KEY = 'ringil.assistant.struck';
const CHANGE_EVENT = 'assistant-struck-changed';
const MAX_ANSWERS = 100;

type StruckMap = Record<string, number[]>;

/** Short, stable id for an answer's text (djb2). Collisions only cost a wrong tick, never data. */
export const answerKey = (markdown: string): string => {
  let hash = 5381;
  for (let i = 0; i < markdown.length; i++) hash = ((hash << 5) + hash + markdown.charCodeAt(i)) | 0;
  return (hash >>> 0).toString(36);
};

let cache: { raw: string | null; value: StruckMap } = { raw: null, value: {} };

const readAll = (): StruckMap => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    // ignore — nothing is struck
  }
  if (raw === cache.raw) return cache.value;
  let value: StruckMap = {};
  try {
    value = raw ? JSON.parse(raw) : {};
  } catch {
    // ignore a corrupted entry
  }
  cache = { raw, value };
  return value;
};

const EMPTY: number[] = [];

export const toggleStruck = (key: string, index: number) => {
  const all = { ...readAll() };
  const current = all[key] ?? EMPTY;
  const next = current.includes(index) ? current.filter(i => i !== index) : [...current, index];
  // Re-inserted last so the oldest answers are the ones dropped once the cap is reached.
  delete all[key];
  if (next.length > 0) all[key] = next;
  const keys = Object.keys(all);
  keys.slice(0, Math.max(0, keys.length - MAX_ANSWERS)).forEach(k => delete all[k]);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    // ignore — the tick just will not survive a reload
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
};

const subscribe = (cb: () => void) => {
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) cb();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(CHANGE_EVENT, cb);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(CHANGE_EVENT, cb);
  };
};

/** The struck row indexes of one answer — a stable array, so it is safe as a useSyncExternalStore snapshot. */
export const useStruckRows = (key: string): number[] =>
  useSyncExternalStore(
    subscribe,
    () => readAll()[key] ?? EMPTY,
    () => EMPTY,
  );
